// Stale-chunk recovery (ICODE-208, Sentry INNOVATIONCODERS-6).
//
// This is a hashed-asset SPA on a rolling deploy. A browser that loaded an OLD
// index.html still references OLD `assets/<chunk>-<hash>.js` files; after a
// redeploy those hashes are gone (404) and a lazy `import()` (route/component
// code-split) rejects with:
//
//   TypeError: Failed to fetch dynamically imported module: .../assets/<chunk>.js
//
// Vite fires a `vite:preloadError` event on `window` for exactly this case. We
// respond with a SINGLE guarded `window.location.reload()` so the browser pulls
// the fresh index.html + current chunk hashes.
//
// The guard is a cooldown timestamp in sessionStorage: we reload at most once
// per cooldown window. If a preload error recurs immediately AFTER a reload,
// that is a genuine network/asset failure (not post-deploy staleness), so we do
// NOT reload again — we let it surface (Sentry still records it). Once the
// window elapses, a later legitimate staleness event (e.g. a second deploy in a
// long-lived tab) can recover again.

const RELOAD_KEY = 'vite-preload-reloaded';
const RELOAD_COOLDOWN_MS = 10_000;

const STALE_CHUNK_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'ChunkLoadError',
];

/** True when an error message looks like a stale/failed dynamic-import chunk load. */
export function isStaleChunkError(message: string | null | undefined): boolean {
  if (!message) return false;
  return STALE_CHUNK_PATTERNS.some((p) => message.includes(p));
}

/**
 * Attempt a guarded one-shot reload to recover from a stale chunk.
 * Returns true if a reload was triggered, false if suppressed by the guard
 * (recent reload still failing, or sessionStorage unavailable).
 */
export function recoverFromStaleChunk(): boolean {
  const now = Date.now();

  let last = 0;
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    last = raw ? Number.parseInt(raw, 10) || 0 : 0;
  } catch {
    // sessionStorage unreadable (e.g. hardened privacy mode): cannot guarantee
    // the loop guard, so refuse to reload rather than risk a hard reload loop.
    return false;
  }

  if (last && now - last < RELOAD_COOLDOWN_MS) {
    // We reloaded moments ago and it STILL failed → genuine failure, not
    // staleness. Let it surface instead of looping.
    return false;
  }

  try {
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    return false;
  }

  // Local visibility; the post-reload page can't retain SDK breadcrumbs, and a
  // genuine (second) failure surfaces to Sentry via the ErrorBoundary anyway.
  console.warn('[stale-chunk] dynamic import failed after deploy — reloading for fresh assets');
  window.location.reload();
  return true;
}

/** Handler for Vite's `vite:preloadError` window event. */
export function handlePreloadError(event: Event): void {
  // Stop Vite's default (which would rethrow) so our guarded reload owns recovery.
  event.preventDefault?.();
  recoverFromStaleChunk();
}

/** Register the `vite:preloadError` listener. Call once at app boot. */
export function registerStaleChunkRecovery(): void {
  window.addEventListener('vite:preloadError', handlePreloadError);
}
